import React, { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { dayjs } from '@personal-trainer-app/shared/src/utils/date';

import type { FooterLinkGroupProps } from './PublicFooter.types';

import { Wordmark } from '@/components/ui/Logo';
import { PUBLIC_ROUTES } from '@/utils';

const FOOTER_LINKS = {
  product: {
    heading: 'Product',
    ariaLabel: 'Product navigation',
    links: [
      { label: 'Features', href: '/#features', internal: false },
      { label: 'Pricing', href: PUBLIC_ROUTES.PRICING, internal: true },
      { label: 'Mobile App', href: PUBLIC_ROUTES.MOBILE_APP, internal: true },
    ],
  },
  company: {
    heading: 'Company',
    ariaLabel: 'Company navigation',
    links: [
      { label: 'About Us', href: PUBLIC_ROUTES.ABOUT, internal: true },
      { label: 'Contact', href: PUBLIC_ROUTES.CONTACT, internal: true },
    ],
  },
  legal: {
    heading: 'Legal',
    ariaLabel: 'Legal navigation',
    links: [
      // One link: the legal page switches between Privacy and Terms itself.
      {
        label: 'Privacy & Terms',
        href: PUBLIC_ROUTES.PRIVACY,
        internal: true,
      },
    ],
  },
} as const;

function FooterLinkGroup({
  heading,
  ariaLabel,
  links,
}: FooterLinkGroupProps): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const handleHashLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      const id = href.replace('/#', '');

      if (location.pathname === '/') {
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate({ pathname: '/', hash: `#${id}` });
      }
    },
    [location.pathname, navigate]
  );

  return (
    <div>
      <h3 className="text-label-md font-bold uppercase tracking-wider text-neutral-50">
        {heading}
      </h3>
      <nav aria-label={ariaLabel} className="mt-4 flex flex-col gap-3">
        {links.map((link) => {
          const isHashLink = link.href.startsWith('/#');

          if (link.internal) {
            return (
              <Link
                key={link.label}
                to={link.href}
                className="text-body-md text-neutral-400 transition-colors hover:text-neutral-50 rounded focus-visible:outline-none focus-visible:ring-state-focus"
              >
                {link.label}
              </Link>
            );
          }

          if (isHashLink) {
            return (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleHashLinkClick(e, link.href)}
                className="text-body-md text-neutral-400 transition-colors hover:text-neutral-50 rounded focus-visible:outline-none focus-visible:ring-state-focus"
              >
                {link.label}
              </a>
            );
          }

          return (
            <a
              key={link.label}
              href={link.href}
              className="text-body-md text-neutral-400 transition-colors hover:text-neutral-50 rounded focus-visible:outline-none focus-visible:ring-state-focus"
            >
              {link.label}
            </a>
          );
        })}
      </nav>
    </div>
  );
}

export function PublicFooter(): React.JSX.Element {
  const currentYear = dayjs().year();

  return (
    <footer className="border-t border-stroke-default bg-contrast-950 py-12 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Main footer grid */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* Brand column */}
          <div className="md:col-span-2 lg:col-span-1">
            <Link
              to={PUBLIC_ROUTES.HOME}
              className="inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-state-focus"
            >
              <Wordmark size={28} />
            </Link>
            <p className="mt-4 text-body-md text-neutral-400">
              Your training journal. Walk the path, track the way.
            </p>
          </div>

          {/* Product links */}
          <FooterLinkGroup {...FOOTER_LINKS.product} />

          {/* Company links */}
          <FooterLinkGroup {...FOOTER_LINKS.company} />

          {/* Legal links */}
          <FooterLinkGroup {...FOOTER_LINKS.legal} />
        </div>

        {/* Divider */}
        <div className="mt-12 border-t border-stroke-default" />

        {/* Bottom bar */}
        <div className="mt-8 flex flex-col items-center gap-4 md:flex-row md:justify-between">
          {/* Copyright */}
          <p className="text-body-sm text-neutral-600 text-center md:text-left">
            &copy; {currentYear} Dō. All rights reserved.
          </p>
          {/* Every user avatar is a Robohash monster (set2), which is CC-BY: credit the service and its artist */}
          <p className="text-body-sm text-neutral-600 text-center md:text-right">
            Avatars by{' '}
            <a
              href="https://robohash.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-neutral-400 transition-colors hover:text-neutral-50 rounded focus-visible:outline-none focus-visible:ring-state-focus"
            >
              Robohash.org
            </a>{' '}
            &middot; art by Hrvoje Novakovic
          </p>
          {/* Social icons are parked for the beta — see PublicFooterSocialLinks */}
        </div>
      </div>
    </footer>
  );
}

export default PublicFooter;
